import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Badge, DataTable, Button, Input, Modal, Tabs } from '../../../components/ui';
import { RefreshCw, Building2, Plus, KeyRound, Power, Layers3, Store, Tag, Pencil, ShieldCheck, Landmark, Users } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

// ── Interfaces ──────────────────────────────────────────────

interface LimiteLocalesInfo {
  max_locales: number;
  usados: number;
  disponibles: number;
}

interface CrearLocalForm {
  nombre: string;
  direccion: string;
  telefono: string;
  es_propio: boolean;
  grupo_id: string;
  limite_saldo: string;
}

interface LocalResumenItem {
  local_id: string;
  nombre_local: string;
  local_username?: string | null;
  local_activo: boolean;
  es_propio: boolean;
  grupo_id?: string | null;
  limite_saldo?: number | null;
  banquero_id?: string | null;
}

interface LocalSimpleItem {
  id: string;
  nombre: string;
  activo: boolean;
  es_propio: boolean;
  grupo_id?: string | null;
}

interface EditarLocalForm {
  es_propio: boolean;
  grupo_id: string;
  limite_saldo: string;
  banquero_id: string;
}

interface CodigoDinamicoData {
  codigo: string;
  expira_at: string;
  segundos_restantes: number;
}

interface UsuarioOperativo {
  id: string;
  username: string;
  nombre: string;
  role: string;
  banca_id?: string;
  activo: boolean;
  created_at?: string;
}

// ── Tab: Locales (contenido original) ───────────────────────

const LocalesTab: React.FC = () => {
  const [localesResumen, setLocalesResumen] = useState<LocalResumenItem[]>([]);
  const [limiteLocales, setLimiteLocales] = useState<LimiteLocalesInfo>({ max_locales: 0, usados: 0, disponibles: 0 });
  const [loading, setLoading] = useState(false);
  const [savingLocal, setSavingLocal] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [selectedLocal, setSelectedLocal] = useState<LocalResumenItem | null>(null);
  const [editingLocal, setEditingLocal] = useState<LocalResumenItem | null>(null);
  const [credForm, setCredForm] = useState({ username: '', password: '' });
  const [editForm, setEditForm] = useState<EditarLocalForm>({ es_propio: false, grupo_id: '', limite_saldo: '', banquero_id: '' });
  const [banquerosDisponibles, setBanquerosDisponibles] = useState<UsuarioOperativo[]>([]);
  const [error, setError] = useState('');
  const [codigoDinamico, setCodigoDinamico] = useState<CodigoDinamicoData | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [copiado, setCopiado] = useState(false);
  const [cargandoCodigo, setCargandoCodigo] = useState(false);
  const [form, setForm] = useState<CrearLocalForm>({
    nombre: '',
    direccion: '',
    telefono: '',
    es_propio: false,
    grupo_id: '',
    limite_saldo: '',
  });

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    try {
      const [resumenData, limiteLocalesData, banquerosData] = await Promise.all([
        fetchAPI<LocalResumenItem[]>('/usuarios/banca/locales/resumen-vendedores'),
        fetchAPI<LimiteLocalesInfo>('/usuarios/banca/locales/limite'),
        fetchAPI<UsuarioOperativo[]>('/usuarios/banca/operativos?role=BANQUERO').catch(() => [] as UsuarioOperativo[]),
      ]);
      setLocalesResumen(resumenData);
      setLimiteLocales(limiteLocalesData);
      setBanquerosDisponibles((banquerosData || []).filter((u) => u.activo));
    } catch (e: any) {
      setError(e.message || 'No se pudo cargar el resumen de locales');
    } finally {
      setLoading(false);
    }
  };

  const crearLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.nombre.trim()) { setError('Nombre del local es obligatorio'); return; }
    setSavingLocal(true);
    try {
      await fetchAPI<LocalSimpleItem>('/usuarios/banca/locales', {
        method: 'POST',
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          direccion: form.direccion.trim() || null,
          telefono: form.telefono.trim() || null,
          es_propio: form.es_propio,
          grupo_id: form.grupo_id.trim() || null,
          limite_saldo: !form.es_propio && form.limite_saldo.trim() ? Number(form.limite_saldo) : null,
        }),
      });
      setModalOpen(false);
      setForm({ nombre: '', direccion: '', telefono: '', es_propio: true, grupo_id: '', limite_saldo: '' });
      await cargarDatos();
    } catch (e: any) { setError(e.message || 'No se pudo crear el local'); } finally { setSavingLocal(false); }
  };

  useEffect(() => { cargarDatos(); }, []);

  const generarCodigoDinamico = async () => {
    setCargandoCodigo(true);
    try {
      const data = await fetchAPI<CodigoDinamicoData>('/auth/codigo-dinamico/regenerar', { method: 'POST' });
      setCodigoDinamico(data);
      setSegundosRestantes(data.segundos_restantes || 0);
    } catch (e: any) { setError(e.message || 'No se pudo generar el código dinámico'); } finally { setCargandoCodigo(false); }
  };

  const copiarCodigo = async () => {
    if (!codigoDinamico?.codigo) return;
    try { await navigator.clipboard.writeText(codigoDinamico.codigo); setCopiado(true); setTimeout(() => setCopiado(false), 1500); }
    catch { setError('No se pudo copiar el código'); }
  };

  useEffect(() => {
    if (segundosRestantes <= 0) return;
    const iv = window.setInterval(() => {
      setSegundosRestantes((prev) => { if (prev <= 1) { setCodigoDinamico(null); return 0; } return prev - 1; });
    }, 1000);
    return () => window.clearInterval(iv);
  }, [segundosRestantes > 0]);

  const formatoTiempo = `${String(Math.floor(segundosRestantes / 60)).padStart(2, '0')}:${String(segundosRestantes % 60).padStart(2, '0')}`;
  const totalLocales = useMemo(() => localesResumen.length, [localesResumen]);
  const totalPropios = useMemo(() => localesResumen.filter((item) => item.es_propio).length, [localesResumen]);
  const totalNoPropios = totalLocales - totalPropios;
  const totalGrupos = useMemo(() => {
    const grupos = new Set(localesResumen.map((item) => (item.grupo_id || '').trim()).filter(Boolean));
    return grupos.size;
  }, [localesResumen]);

  const abrirCredenciales = (local: LocalResumenItem) => { setSelectedLocal(local); setCredForm({ username: local.local_username || '', password: '' }); setCredModalOpen(true); setError(''); };
  const abrirEditarLocal = (local: LocalResumenItem) => { setEditingLocal(local); setEditForm({ es_propio: local.es_propio, grupo_id: local.grupo_id || '', limite_saldo: local.limite_saldo != null ? String(local.limite_saldo) : '', banquero_id: local.banquero_id || '' }); setError(''); setEditModalOpen(true); };

  const guardarEdicionLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLocal) return;
    setSavingAction(true); setError('');
    try {
      await fetchAPI<LocalResumenItem>(`/usuarios/banca/locales/${editingLocal.local_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ es_propio: editForm.es_propio, grupo_id: editForm.grupo_id.trim() || null, limite_saldo: !editForm.es_propio && editForm.limite_saldo.trim() ? Number(editForm.limite_saldo) : null, banquero_id: editForm.banquero_id || null }),
      });
      setEditModalOpen(false); setEditingLocal(null); await cargarDatos();
    } catch (e: any) { setError(e.message || 'No se pudo actualizar el local'); } finally { setSavingAction(false); }
  };

  const guardarCredenciales = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocal) return;
    const username = credForm.username.trim();
    const password = credForm.password.trim();
    if (!username && !password) { setError('Debes indicar un usuario y/o contraseña'); return; }
    setSavingAction(true); setError('');
    try {
      await fetchAPI<LocalResumenItem>(`/usuarios/banca/locales/${selectedLocal.local_id}/credenciales`, {
        method: 'PATCH',
        body: JSON.stringify({ username: username || undefined, password: password || undefined }),
      });
      setCredModalOpen(false); setSelectedLocal(null); setCredForm({ username: '', password: '' }); await cargarDatos();
    } catch (e: any) { setError(e.message || 'No se pudieron actualizar las credenciales del local'); } finally { setSavingAction(false); }
  };

  const cambiarEstadoLocal = async (local: LocalResumenItem) => {
    const accion = local.local_activo ? 'inactivar' : 'activar';
    const mensaje = local.local_activo ? `¿Seguro que deseas inactivar ${local.nombre_local}?` : `¿Seguro que deseas activar ${local.nombre_local}?`;
    if (!window.confirm(mensaje)) return;
    setSavingAction(true); setError('');
    try {
      await fetchAPI<LocalResumenItem>(`/usuarios/banca/locales/${local.local_id}/estado`, { method: 'PATCH', body: JSON.stringify({ activo: !local.local_activo }) });
      await cargarDatos();
    } catch (e: any) { setError(e.message || `No se pudo ${accion} el local`); } finally { setSavingAction(false); }
  };

  const columns = [
    {
      key: 'local', header: 'Local',
      render: (l: LocalResumenItem) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"><Building2 className="w-5 h-5 text-gray-500" /></div>
          <div><p className="font-medium text-gray-900">{l.nombre_local}</p><p className="text-sm text-gray-500">{l.local_id}</p></div>
        </div>
      ),
    },
    { key: 'tipo', header: 'Tipo', render: (l: LocalResumenItem) => <Badge variant={l.es_propio ? 'success' : 'neutral'}>{l.es_propio ? 'PROPIO' : 'NO PROPIO'}</Badge> },
    { key: 'grupo', header: 'Grupo saldo', render: (l: LocalResumenItem) => <span className="font-medium text-gray-700">{l.grupo_id || 'Sin grupo'}</span> },
    { key: 'usuario_local', header: 'Usuario local', render: (l: LocalResumenItem) => <span className="font-medium">{l.local_username || '-'}</span> },
    { key: 'estado', header: 'Estado', render: (l: LocalResumenItem) => <Badge variant={l.local_activo ? 'success' : 'neutral'}>{l.local_activo ? 'ACTIVO' : 'INACTIVO'}</Badge> },
    {
      key: 'acciones', header: 'Acciones',
      render: (l: LocalResumenItem) => (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <Button size="sm" variant="secondary" leftIcon={<Pencil size={14} />} onClick={() => abrirEditarLocal(l)} disabled={savingAction}><span className="hidden sm:inline">Editar</span></Button>
          <Button size="sm" variant="secondary" leftIcon={<KeyRound size={14} />} onClick={() => abrirCredenciales(l)} disabled={savingAction}><span className="hidden sm:inline">Credenciales</span></Button>
          <Button size="sm" variant={l.local_activo ? 'warning' : 'success'} leftIcon={<Power size={14} />} onClick={() => cambiarEstadoLocal(l)} disabled={savingAction}><span className="hidden sm:inline">{l.local_activo ? 'Inactivar' : 'Activar'}</span></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div />
          <div className="flex items-center gap-2">
            <button onClick={cargarDatos} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Refrescar">
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <Button variant="primary" leftIcon={<Plus size={18} />} onClick={() => setModalOpen(true)} disabled={limiteLocales.disponibles <= 0}>Nuevo Local</Button>
          </div>
        </div>

        <Card className="w-full">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Código dinámico</span>
              {segundosRestantes > 0 && <span className="text-xs text-gray-500">{formatoTiempo}</span>}
            </div>
            {codigoDinamico ? (
              <>
                <div className="text-3xl font-bold tracking-[0.2em] text-gray-900 text-center">{codigoDinamico.codigo}</div>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={copiarCodigo}>{copiado ? 'Copiado' : 'Copiar'}</Button>
                  <Button type="button" size="sm" variant="secondary" onClick={generarCodigoDinamico} isLoading={cargandoCodigo}>Regenerar</Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-400 text-center py-1">{segundosRestantes <= 0 && codigoDinamico === null ? 'Sin código activo' : '------'}</div>
                <Button type="button" size="sm" variant="primary" onClick={generarCodigoDinamico} isLoading={cargandoCodigo} className="w-full">Generar código</Button>
              </>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center"><Store className="w-5 h-5 text-blue-600" /></div><div><p className="text-sm text-gray-500">Locales</p><p className="text-2xl font-bold text-gray-900">{totalLocales}</p></div></div></Card>
        <Card><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center"><Tag className="w-5 h-5 text-green-600" /></div><div><p className="text-sm text-gray-500">Propios</p><p className="text-2xl font-bold text-green-600">{totalPropios}</p></div></div></Card>
        <Card><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-gray-600" /></div><div><p className="text-sm text-gray-500">No propios</p><p className="text-2xl font-bold text-gray-700">{totalNoPropios}</p></div></div></Card>
        <Card><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center"><Layers3 className="w-5 h-5 text-purple-600" /></div><div><p className="text-sm text-gray-500">Grupos configurados</p><p className="text-2xl font-bold text-purple-600">{totalGrupos}</p></div></div></Card>
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <Card padding="none">
        <DataTable columns={columns} data={localesResumen} keyExtractor={(l: LocalResumenItem) => l.local_id} isLoading={loading} emptyMessage="No hay locales registrados en esta banca" />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Crear nuevo local" size="md">
        <form onSubmit={crearLocal} className="space-y-4">
          <Input label="Nombre" value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} required />
          <Input label="Dirección (opcional)" value={form.direccion} onChange={(e) => setForm((p) => ({ ...p, direccion: e.target.value }))} />
          <Input label="Teléfono (opcional)" value={form.telefono} onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))} />
          <Input label="Grupo de saldo (opcional)" value={form.grupo_id} onChange={(e) => setForm((p) => ({ ...p, grupo_id: e.target.value }))} placeholder="Ej: Grupo A" />
          <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.es_propio} onChange={(e) => setForm((prev) => ({ ...prev, es_propio: e.target.checked, limite_saldo: e.target.checked ? '' : prev.limite_saldo }))} className="h-4 w-4" />
            Local propio (imprime logo en ticket)
          </label>
          {!form.es_propio && <Input label="Límite de saldo/crédito (opcional)" type="number" value={form.limite_saldo} onChange={(e) => setForm((p) => ({ ...p, limite_saldo: e.target.value }))} placeholder="Ej: 100000" />}
          <div className="rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-800">Cada local crea su propio usuario operativo y funciona como una unidad de venta independiente.</div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingLocal}>Crear local</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={editModalOpen} onClose={() => { setEditModalOpen(false); setEditingLocal(null); }} title={`Editar local${editingLocal ? `: ${editingLocal.nombre_local}` : ''}`} size="md">
        <form onSubmit={guardarEdicionLocal} className="space-y-4">
          <Input label="Grupo de saldo" value={editForm.grupo_id} onChange={(e) => setEditForm((prev) => ({ ...prev, grupo_id: e.target.value }))} placeholder="Ej: Grupo A" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banquero asignado</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              value={editForm.banquero_id}
              onChange={(e) => setEditForm((prev) => ({ ...prev, banquero_id: e.target.value }))}
            >
              <option value="">— Sin banquero —</option>
              {banquerosDisponibles.map((b) => (
                <option key={b.id} value={b.id}>{b.nombre || b.username} ({b.username})</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Cada local pertenece a un solo banquero. Los locales sin banquero no aparecen en ninguna cuenta de banquero.</p>
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input type="checkbox" checked={editForm.es_propio} onChange={(e) => setEditForm((prev) => ({ ...prev, es_propio: e.target.checked, limite_saldo: e.target.checked ? '' : prev.limite_saldo }))} className="h-4 w-4" />
            Local propio (imprime logo en ticket)
          </label>
          {!editForm.es_propio && <Input label="Límite de saldo/crédito (opcional)" type="number" value={editForm.limite_saldo} onChange={(e) => setEditForm((prev) => ({ ...prev, limite_saldo: e.target.value }))} placeholder="Ej: 100000" />}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setEditModalOpen(false); setEditingLocal(null); }}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingAction}>Guardar cambios</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={credModalOpen} onClose={() => { setCredModalOpen(false); setSelectedLocal(null); setCredForm({ username: '', password: '' }); }} title={`Credenciales local${selectedLocal ? `: ${selectedLocal.nombre_local}` : ''}`} size="md">
        <form onSubmit={guardarCredenciales} className="space-y-4">
          <Input label="Usuario" value={credForm.username} onChange={(e) => setCredForm((p) => ({ ...p, username: e.target.value }))} placeholder="Usuario del local" />
          <Input label="Nueva contraseña (opcional)" type="password" value={credForm.password} onChange={(e) => setCredForm((p) => ({ ...p, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setCredModalOpen(false); setSelectedLocal(null); setCredForm({ username: '', password: '' }); }}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={savingAction}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ── Tab: Operativos (Supervisores / Banqueros) ──────────────

const OperativosTab: React.FC<{ roleFiltro: 'SUPERVISOR' | 'BANQUERO' }> = ({ roleFiltro }) => {
  const [usuarios, setUsuarios] = useState<UsuarioOperativo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UsuarioOperativo | null>(null);
  const [credForm, setCredForm] = useState({ username: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', nombre: '' });

  const etiqueta = roleFiltro === 'SUPERVISOR' ? 'Supervisor' : 'Banquero';

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<UsuarioOperativo[]>(`/usuarios/banca/operativos?role=${roleFiltro}`);
      setUsuarios(data);
    } catch (e: any) {
      setError(e.message || `No se pudieron cargar los ${etiqueta.toLowerCase()}es`);
    } finally {
      setLoading(false);
    }
  }, [roleFiltro, etiqueta]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const crearUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.nombre.trim()) { setError('Nombre es obligatorio'); return; }
    if (!form.username.trim()) { setError('Usuario es obligatorio'); return; }
    if (form.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setSaving(true);
    try {
      await fetchAPI<UsuarioOperativo>('/usuarios/banca/operativos', {
        method: 'POST',
        body: JSON.stringify({ username: form.username.trim(), password: form.password, nombre: form.nombre.trim(), role: roleFiltro }),
      });
      setModalOpen(false);
      setForm({ username: '', password: '', nombre: '' });
      await cargarDatos();
    } catch (e: any) { setError(e.message || `No se pudo crear el ${etiqueta.toLowerCase()}`); } finally { setSaving(false); }
  };

  const abrirCredenciales = (u: UsuarioOperativo) => {
    setSelectedUser(u);
    setCredForm({ username: u.username, password: '' });
    setCredModalOpen(true);
    setError('');
  };

  const guardarCredenciales = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const username = credForm.username.trim();
    const password = credForm.password.trim();
    if (!username && !password) { setError('Debe indicar un usuario y/o contraseña'); return; }
    setSaving(true); setError('');
    try {
      await fetchAPI(`/usuarios/banca/operativos/${selectedUser.id}/credenciales`, {
        method: 'PATCH',
        body: JSON.stringify({ username: username || undefined, password: password || undefined }),
      });
      setCredModalOpen(false); setSelectedUser(null); setCredForm({ username: '', password: '' }); await cargarDatos();
    } catch (e: any) { setError(e.message || 'No se pudieron actualizar las credenciales'); } finally { setSaving(false); }
  };

  const cambiarEstado = async (u: UsuarioOperativo) => {
    const accion = u.activo ? 'inactivar' : 'activar';
    if (!window.confirm(`¿Seguro que deseas ${accion} a ${u.nombre}?`)) return;
    setSaving(true); setError('');
    try {
      await fetchAPI(`/usuarios/banca/operativos/${u.id}/estado`, { method: 'PATCH', body: JSON.stringify({ activo: !u.activo }) });
      await cargarDatos();
    } catch (e: any) { setError(e.message || `No se pudo ${accion} el usuario`); } finally { setSaving(false); }
  };

  const columns = [
    {
      key: 'nombre', header: 'Nombre',
      render: (u: UsuarioOperativo) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleFiltro === 'SUPERVISOR' ? 'bg-blue-50' : 'bg-amber-50'}`}>
            {roleFiltro === 'SUPERVISOR' ? <ShieldCheck className="w-5 h-5 text-blue-600" /> : <Landmark className="w-5 h-5 text-amber-600" />}
          </div>
          <div><p className="font-medium text-gray-900">{u.nombre}</p><p className="text-sm text-gray-500">@{u.username}</p></div>
        </div>
      ),
    },
    { key: 'estado', header: 'Estado', render: (u: UsuarioOperativo) => <Badge variant={u.activo ? 'success' : 'neutral'}>{u.activo ? 'ACTIVO' : 'INACTIVO'}</Badge> },
    {
      key: 'acciones', header: 'Acciones',
      render: (u: UsuarioOperativo) => (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <Button size="sm" variant="secondary" leftIcon={<KeyRound size={14} />} onClick={() => abrirCredenciales(u)} disabled={saving}><span className="hidden sm:inline">Credenciales</span></Button>
          <Button size="sm" variant={u.activo ? 'warning' : 'success'} leftIcon={<Power size={14} />} onClick={() => cambiarEstado(u)} disabled={saving}><span className="hidden sm:inline">{u.activo ? 'Inactivar' : 'Activar'}</span></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{usuarios.length} {etiqueta.toLowerCase()}(es) registrado(s)</p>
        <div className="flex items-center gap-2">
          <button onClick={cargarDatos} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Refrescar">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <Button variant="primary" leftIcon={<Plus size={18} />} onClick={() => setModalOpen(true)}>Nuevo {etiqueta}</Button>
        </div>
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <Card padding="none">
        <DataTable columns={columns} data={usuarios} keyExtractor={(u: UsuarioOperativo) => u.id} isLoading={loading} emptyMessage={`No hay ${etiqueta.toLowerCase()}es registrados`} />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={`Crear ${etiqueta}`} size="md">
        <form onSubmit={crearUsuario} className="space-y-4">
          <Input label="Nombre completo" value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} required />
          <Input label="Usuario" value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} required />
          <Input label="Contraseña" type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} required placeholder="Mínimo 6 caracteres" />
          <div className="rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-800">
            {roleFiltro === 'SUPERVISOR'
              ? 'El supervisor podrá ver listas, ventas, saldos y asignar ganadores. No tendrá acceso a configuración ni reportes financieros.'
              : 'El banquero podrá ver información consolidada de los locales agrupada por paquete de reglas.'}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={saving}>Crear {etiqueta.toLowerCase()}</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={credModalOpen} onClose={() => { setCredModalOpen(false); setSelectedUser(null); }} title={`Credenciales: ${selectedUser?.nombre || ''}`} size="md">
        <form onSubmit={guardarCredenciales} className="space-y-4">
          <Input label="Usuario" value={credForm.username} onChange={(e) => setCredForm((p) => ({ ...p, username: e.target.value }))} />
          <Input label="Nueva contraseña (opcional)" type="password" value={credForm.password} onChange={(e) => setCredForm((p) => ({ ...p, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setCredModalOpen(false); setSelectedUser(null); }}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={saving}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ── Página principal con tabs ───────────────────────────────

export const BancaVendedoresPage: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestión de usuarios</h1>
        <p className="text-gray-500">Administra locales, supervisores y banqueros de tu banca</p>
      </div>

      <Tabs
        tabs={[
          {
            id: 'locales',
            label: 'Locales',
            icon: <Store size={16} />,
            content: <LocalesTab />,
          },
          {
            id: 'supervisores',
            label: 'Supervisores',
            icon: <ShieldCheck size={16} />,
            content: <OperativosTab roleFiltro="SUPERVISOR" />,
          },
          {
            id: 'banqueros',
            label: 'Banqueros',
            icon: <Landmark size={16} />,
            content: <OperativosTab roleFiltro="BANQUERO" />,
          },
        ]}
        defaultTab="locales"
      />
    </div>
  );
};

export default BancaVendedoresPage;
